import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'redux/store'
import { PaymentMethod } from 'models/ChargeIntent'
import { setPaymentProcessor, setPaymentType } from 'redux/features/paymentDetails'
import { getAnalyticsInstance } from 'modules/analytics/analytics'
import {
  getPaymentMethodConfig,
  useActivePaymentMethodConfigs,
} from '../utils/use-payment-method-configs'

export const usePaymentMethodSelection = () => {
  const dispatch = useDispatch()
  const analytics = getAnalyticsInstance()
  const [isLoading, setIsLoading] = useState(true)

  const selectedPaymentMethod = useSelector((state: RootState) => state.order.paymentDetails.type)

  const selectedPaymentMethodConfig = getPaymentMethodConfig(selectedPaymentMethod, dispatch)

  const activePaymentMethodConfigs = useActivePaymentMethodConfigs()

  // Handle selection of payment methods
  const onSelect = useCallback(
    (value: PaymentMethod) => {
      const config = activePaymentMethodConfigs.find((config) => config.key === value)

      if (config && config.processor) {
        // Dispatch actions to set the payment type and processor
        dispatch(setPaymentType(value))
        dispatch(setPaymentProcessor(config.processor))

        // Track payment processor in analytics
        if (analytics && analytics.storage) {
          analytics.storage.setItem('paymentProcessor', config.processor)
        }
      } else {
        console.error(`No valid processor found for payment method: ${value}`)
      }
    },
    [activePaymentMethodConfigs, analytics, dispatch],
  )

  // Monitor the active payment methods and set loading state accordingly
  useEffect(() => {
    if (activePaymentMethodConfigs.length > 0) {
      setIsLoading(false)
    }
  }, [activePaymentMethodConfigs])

  // Ensure the selected payment method is selected after loading
  useEffect(() => {
    if (!isLoading && selectedPaymentMethod) {
      onSelect(selectedPaymentMethod)
    }
  }, [isLoading, onSelect, selectedPaymentMethod])

  return {
    onSelect,
    isLoading,
    selectedPaymentMethod,
    selectedPaymentMethodConfig,
    activePaymentMethodConfigs,
  }
}
